import React from 'react';
import MaterialTable from '@material-table/core';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import Tooltip from '@mui/material/Tooltip';
import IconButton from '@mui/material/IconButton';
import Paper from '@mui/material/Paper';

const sources = ['FOM', 'FORWARDER','JDLINK', 'MAINCHAIN', 'CONTAINER'];
const milestones = ['ATA', 'ATD', 'ETA', 'ETD'];
const priorityOrder = ['FOM', 'FORWARDER', 'JDLINK', 'MAINCHAIN', 'CONTAINER'];

const colorMapping = {
    FOM: '#FFFFFF',
    JDLINK: '#C1E1C1',
    MAINCHAIN: '#ADD8E6',
    FORWARDER : '#f76a6a',
    CONTAINER: '#C3B1E1',
};

const sourceDisplayNames = {
    FOM: 'FOM',
    JDLINK: 'JDLINK',
    MAINCHAIN: 'MAINCHAIN',
    FORWARDER: 'FORWARDER',
    CONTAINER: 'VESSEL_FINDER',
};

const formatDate = (date) => {
    if (!date) return '-';
    const parsedDate = new Date(date);
    if (isNaN(parsedDate)) return '-';
    const year = parsedDate.getFullYear();
    const month = String(parsedDate.getMonth() + 1).padStart(2, '0');
    const day = String(parsedDate.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
};

const getPrioritizedDateAndSource = (tabValue, milestone) => {
    for (const source of priorityOrder) {
        const value = tabValue[`${milestone}_${source}`];
        if (value) {
            return { date: formatDate(value), source };
        }
    }
    return { date: '-', source: null };
};

const DateMatrixComponent = ({ tabValue }) => {
    // Build data rows: one per source
    const data = sources.map(source => ({
        source: sourceDisplayNames[source],
        ATA: formatDate(tabValue[`ATA_${source}`]),
        ATD: formatDate(tabValue[`ATD_${source}`]),
        ETA: formatDate(tabValue[`ETA_${source}`]),
        ETD: formatDate(tabValue[`ETD_${source}`]),
    }));

    // Build Prioritized Date row with per-cell color
    const prioritizedRow = { source: 'Prioritized Date' };
    const prioritizedColors = {};
    milestones.forEach(milestone => {
        const { date, source } = getPrioritizedDateAndSource(tabValue, milestone);
        prioritizedRow[milestone] = date;
        prioritizedColors[milestone] = source ? colorMapping[source] : '#D3D3D3';
    });
    prioritizedRow._isPrioritized = true;

    const tableData = [prioritizedRow, ...data];

    // Info icon tooltip content
    const infoTooltip = (
        <div>
            <span className="text-[#FFFFFF]">FOM: White</span><br />
            <span className="text-[#FFCCCB]">FORWARDER: Red</span><br />
            <span className="text-[#C1E1C1]">JDLINK: Green</span><br />
            <span className="text-[#ADD8E6]">MAINCHAIN: Blue</span><br />
            <span className="text-[#C3B1E1]">VESSEL_FINDER: Purple</span>
        </div>
    );

    const columns = [
        {
            title: (
                <span className="flex items-center justify-center w-full">
                    Source
                    <Tooltip title={infoTooltip} arrow placement="top">
                        <IconButton size="small" className="ml-1">
                            <InfoOutlinedIcon fontSize="inherit" />
                        </IconButton>
                    </Tooltip>
                </span>
            ),
            field: 'source',
            render: rowData => (
                <span className={rowData._isPrioritized ? 'font-bold' : ''}>{rowData.source}</span>
            ),
        },
        ...milestones.map(milestone => ({
            title: milestone,
            field: milestone,
            render: rowData => {
                if (rowData._isPrioritized) {
                    return (
                        <span
                            className="font-bold block text-center rounded"
                            style={{ backgroundColor: prioritizedColors[milestone] }}
                        >
                            {rowData[milestone]}
                        </span>
                    );
                }
                return (
                    <span className="bg-white block text-center rounded">{rowData[milestone]}</span>
                );
            },
        })),
    ];

    return (
        <Paper className="p-2 sm:p-4 w-full overflow-x-auto" elevation={2}>
            <div className="w-full min-w-[350px] max-w-full overflow-x-auto">
                <div className="w-full">
                    <MaterialTable
                        columns={columns}
                        data={tableData}
                        options={{
                            search: false,
                            paging: false,
                            sorting: false,
                            draggable: false,
                            headerStyle: {
                                backgroundColor: 'white',
                                color: 'black',
                                border: 'none',
                            },
                            rowStyle: rowData => ({
                                backgroundColor: rowData._isPrioritized ? '#F5F5F5' : 'white',
                                fontWeight: rowData._isPrioritized ? 'bold' : 'normal',
                                border: 'none',
                            }),
                            maxBodyHeight: '300px',
                            toolbar: false,
                            tableLayout: 'auto',
                            filterCellStyle: {
                                flex: 1
                            },
                        }}
                        style={{ boxShadow: 'none', textAlign: 'left' }}
                        title="Milestone Dates by Source"
                    />
                </div>
            </div>
         </Paper>
    );
};

export default DateMatrixComponent;